import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useActivateMultiFactorAuthenticationMutation} from '@dms/api/base';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

const schema = z.object({
  gracePeriod: z.number(),
});

type FormType = z.infer<typeof schema>;

export function GracePeriodForm() {
  const [activateMFA] = useActivateMultiFactorAuthenticationMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (values) => {
    await activateMFA({availableLoginTypes: ['totp'], userGracePeriodInDays: values.gracePeriod})
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<FormType>
      experimentalZodSchema={schema}
      onSubmit={handleSubmit}
      defaultValues={{
        gracePeriod: 30,
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <Text size="small">{i18n.t('page.settings.labels.gracePeriodDescription')}</Text>
          <FormField
            control={control}
            name="gracePeriod"
            type="choice"
            label={i18n.t('page.settings.labels.gracePeriodLabel')}
            options={[
              {value: 5, label: `5 ${i18n.t('general.labels.moreDays')}`},
              {value: 10, label: `10 ${i18n.t('general.labels.moreDays')}`},
              {value: 30, label: `30 ${i18n.t('general.labels.moreDays')}`},
              {value: 60, label: `60 ${i18n.t('general.labels.moreDays')}`},
              {value: 90, label: `90 ${i18n.t('general.labels.moreDays')}`},
            ]}
            isRequired
            menuInPortal
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={closeCurrentDialog}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
